import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "system_admin")) navigate("/admin");
    else if (user && user.role === "owner") navigate("/owner");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin" && u.role !== "system_admin") { toast.error("This account is not an administrator."); return; }
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-forest flex items-center justify-center px-5 grain relative">
      <a href="/" data-testid="login-home-link" className="absolute top-5 left-5 text-cream/80 text-sm hover:text-gold z-10">← Back to website</a>
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-9" data-testid="admin-login">
        <div className="text-center mb-7">
          <div className="mx-auto h-12 w-12 rounded-full bg-rose/10 flex items-center justify-center"><Lock className="text-rose" /></div>
          <h1 className="font-serif text-3xl text-forest mt-4">Admin Portal</h1>
          <p className="text-sm text-stone-500 mt-1">The Rose Air BnB management</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <input required type="email" placeholder="Email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
          <input required type="password" placeholder="Password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30" />
          <button type="submit" disabled={loading} data-testid="admin-login-button" className="btn-primary w-full disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={16} /> : "Sign in"}
          </button>
        </form>
        <div className="flex items-center gap-3 my-5"><div className="h-px bg-stone-200 flex-1" /><span className="text-xs text-stone-400">or</span><div className="h-px bg-stone-200 flex-1" /></div>
        <button onClick={googleLogin} data-testid="admin-google-button"
          className="w-full flex items-center justify-center gap-2 rounded-full border border-stone-300 py-3 text-sm font-semibold text-stone-700 hover:bg-stone-50 transition-colors">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.5 30.2 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.8 6c1.9-5.6 7.1-9.8 13.7-9.8z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7c4.3-3.9 6.8-9.7 6.8-16.4z"/><path fill="#FBBC05" d="M10.3 28.3c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.8-6C.9 16.1 0 19.9 0 23.7s.9 7.6 2.5 10.6l7.8-6z"/><path fill="#34A853" d="M24 48c6.2 0 11.5-2 15.3-5.5l-7.3-5.7c-2 1.4-4.7 2.3-8 2.3-6.6 0-11.8-4.2-13.7-9.8l-7.8 6C6.4 42.6 14.6 48 24 48z"/></svg>
          Continue with Google
        </button>
        <p className="text-[11px] text-stone-400 text-center mt-4">Google access is limited to authorised administrators.</p>
      </div>
    </div>
  );
}
